import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy,
  Suspense,
} from "react";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import CloseIcon from "@mui/icons-material/Close";
import BulkEditNodeListDialog from "common/Components/BulkEditNodeListDialog";
import DataTable, { DataTableEmptyState } from "common/Components/DataTable";
import { TOOLBAR_BUTTON_LABEL_CLASS } from "common/Components/DataTable/tableToolbarButton.styles";
import PageTitle from "common/Components/PageTitle/PageTitle";
import { USER_PAGE_TOAST } from "common/Constants/UserPageToast.constants";
import { useUserPageToast } from "common/hooks/UseUserPageToast";
import NodeListDetailsDrawer from "./NodeListDetailsDrawer";
import NodeListExportCsvMenu from "./NodeListExportCsvMenu";
import UserContext from "common/Context/UserContext";
import { useEffectiveAccess } from "common/Context/EffectiveAccessContext";
import { hasDataManagementAccess } from "common/Utilities/EffectiveAccess.utils";
import {
  isFiniteNumber,
  isNullUndefinedOrEmptyString,
} from "common/utils/valueChecks";
import { validateNodeNameForSave } from "common/utils/nodeNameValidation";
import {
  TagsAliasPathText,
  TagsCountBadge,
  TagsDimCellText,
  TagsMonoCellText,
  TagsNodeNameCellText,
  TagsPageRoot,
  TagsScopeTab,
  TagsScopeTabBadge,
  TagsScopeTabs,
  TagsTableFrame,
  ToolbarClearFiltersButton,
} from "./NodeList.styles";
import {
  buildPatchBodyForBulkUpdate,
  buildNodeTypeOptions,
  buildSubtypeOptionsByNodeType,
  buildTagSubtypeOptions,
  buildUnifiedContextualizeTagsFromRows,
  collectUnionOfAllowedParentTypes,
  mapTagSummaryToRow,
  parentNodeFromContextPath,
  mergeParentCandidatesFromTagRows,
  isTagsBulkEditSelectionAllowed,
  canBulkContextualizeTagRow,
} from "./nodeListRowsMapper";
import {
  BULK_CONTEXTUALIZE_MAX_TAGS,
  bulkContextualizeUnifiedTags,
  extractPagedNodesList,
  extractTotalRowCountFromPagedPayload,
  fetchNodeTypesReference,
  fetchParentCandidates,
  fetchTagsPage,
  patchContextNodes,
  resolveContextNodeIdForTagRowAsync,
  extractJobIdFromResponse,
  extractExportJobState,
  getJobExportDownloadUrl,
  getJobExportFileName,
  TAGS_EXPORT_JOB_STATES,
  pollPlatformJobUntilTerminal,
  postExportJob,
  readJobApiErrorMessage,
  readJobFailureMessage,
  readBulkContextualizeResultTagKey,
} from "./nodeListApi";
import {
  TAGS_COLUMN_WIDTHS,
  TAGS_EXPORT_SOURCE_OPTIONS,
  TAGS_SCOPE,
  TAGS_SCOPE_TABS,
  TAGS_TABLE_MIN_WIDTH,
  TAGS_UI,
} from "./NodeList.constants";

const Toast = lazy(() => import("common/Components/Toast"));

const BULK_EDIT_BLOCKED_TOOLTIP =
  "Bulk edit: choose contextual rows that share the same node type, or only uncontextualized rows to contextualize together.";

const FILTER_SELECT_ALL_SENTINEL = "__all__";
const DEFAULT_SORT = "displayName,asc";

const SORT_COLUMN_TO_API = {
  sourceSystem: "source",
  nodeName: "displayName",
  sourceTag: "sourceTag",
  nodeType: "nodeType",
  parentNode: "context",
};

function dimText(value) {
  return (
    <TagsDimCellText variant="body2" title={value != null ? String(value) : ""}>
      {isNullUndefinedOrEmptyString(value) ? "—" : String(value)}
    </TagsDimCellText>
  );
}

function nodeNameText(value) {
  return (
    <TagsNodeNameCellText
      variant="body2"
      title={value != null ? String(value) : ""}
    >
      {isNullUndefinedOrEmptyString(value) ? "—" : String(value)}
    </TagsNodeNameCellText>
  );
}

function monoText(value) {
  return (
    <TagsMonoCellText variant="body2" title={value != null ? String(value) : ""}>
      {isNullUndefinedOrEmptyString(value) ? "—" : String(value)}
    </TagsMonoCellText>
  );
}

function aliasPathCell(row) {
  const derived = row?.uncontextualized ? "—" : (row?.contextTag || row?.context);
  return (
    <TagsAliasPathText
      variant="body2"
      $contextualized={Boolean(row?.contextTag || row?.context)}
      title={derived}
    >
      {derived}
    </TagsAliasPathText>
  );
}

function buildTagColumns({
  nodeTypeOptions,
  allSubtypeOptions,
  customerName,
}) {
  return [
    {
      id: "sourceSystem",
      label: "Connector",
      sortable: true,
      width: TAGS_COLUMN_WIDTHS.sourceSystem,
      filter: { type: "text" },
      render: (v) => dimText(v),
    },
    {
      id: "nodeName",
      label: "Source Name",
      sortable: true,
      width: TAGS_COLUMN_WIDTHS.nodeName,
      filter: { type: "text" },
      render: (v) => nodeNameText(v),
    },
    {
      id: "sourceTag",
      label: "Source Path",
      sortable: true,
      filter: { type: "text" },
      render: (v) => monoText(v),
    },
    {
      id: "nodeType",
      label: "Node Type",
      sortable: true,
      width: TAGS_COLUMN_WIDTHS.nodeType,
      filter:
        nodeTypeOptions.length > 0
          ? { type: "select", options: nodeTypeOptions }
          : { type: "text" },
      render: (v) => dimText(v),
    },
    {
      id: "nodeSubType",
      label: "Node Subtype",
      sortable: true,
      width: TAGS_COLUMN_WIDTHS.nodeSubType,
      filter:
        allSubtypeOptions.length > 0
          ? { type: "select", options: allSubtypeOptions }
          : { type: "text" },
      render: (v) => dimText(v),
    },
    {
      id: "parentNode",
      label: "Node Parent",
      sortable: true,
      width: TAGS_COLUMN_WIDTHS.parentNode,
      filter: { type: "text" },
      render: (_cellValue, row) => {
        if (row?.uncontextualized) {
          return monoText("");
        }
        return monoText(parentNodeFromContextPath(row?.context, customerName));
      },
    },
    {
      id: "contextTag",
      label: "Context Path",
      sortable: true,
      width: TAGS_COLUMN_WIDTHS.contextTag,
      filter: { type: "text" },
      render: (_v, row) => aliasPathCell(row),
    },
  ];
}

async function triggerExportDownload(url, filename) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Download failed (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function formatApiError(result) {
  if (typeof result.error === "string" && result.error.trim()) {
    return result.error;
  }
  const data = result.data;
  if (data && typeof data === "object" && data.success === false) {
    const failed = data.results?.find((r) => r.status !== "updated");
    if (failed?.error) return failed.error;
  }
  return "Request failed. Please try again.";
}

function formatBulkContextualizeSummary(data) {
  if (!data || typeof data !== "object") {
    return "Bulk contextualize completed with an unknown response.";
  }
  const created = data.created ?? 0;
  const failed = data.failed ?? 0;
  if (data.success === true) {
    return `Successfully contextualized ${created} node(s).`;
  }
  const parts = [`Created ${created}, failed ${failed}.`];
  if (Array.isArray(data.results)) {
    for (const r of data.results) {
      if (String(r?.status) === "failed") {
        parts.push(
          `${r.source ?? ""} ${readBulkContextualizeResultTagKey(r)}: ${r.error ?? "Failed"}`
        );
      }
    }
  }
  return parts.join(" ");
}

function toNonEmptyString(value) {
  if (value == null) return "";
  return String(value).trim();
}

function hasActiveTagFilters(filters) {
  return Object.values(filters ?? {}).some((value) => {
    const text = toNonEmptyString(value);
    return text !== "" && text !== FILTER_SELECT_ALL_SENTINEL;
  });
}

function resolveParentNodeNameFilter(parentFilter) {
  const parentFilterString = toNonEmptyString(parentFilter);
  if (parentFilterString === "" || parentFilter === FILTER_SELECT_ALL_SENTINEL) {
    return "";
  }
  return parentFilterString;
}

const SCOPE_CONTEXTUALIZED_FLAG = {
  [TAGS_SCOPE.CONTEXTUALIZED]: true,
  [TAGS_SCOPE.UNCONTEXTUALIZED]: false,
};

function nonNegative(value) {
  return Math.max(value, 0);
}

function deriveScopeCounts(scope, scopeTotal, probeTotal) {
  if (scope === TAGS_SCOPE.CONTEXTUALIZED) {
    return {
      all: probeTotal,
      contextualized: scopeTotal,
      uncontextualized: nonNegative(probeTotal - scopeTotal),
    };
  }
  if (scope === TAGS_SCOPE.UNCONTEXTUALIZED) {
    return {
      all: probeTotal,
      contextualized: nonNegative(probeTotal - scopeTotal),
      uncontextualized: scopeTotal,
    };
  }
  return {
    all: scopeTotal,
    contextualized: probeTotal,
    uncontextualized: nonNegative(scopeTotal - probeTotal),
  };
}

function buildTagListApiQuery(filters, page, size, sort, scope) {
  const parentNodeName = resolveParentNodeNameFilter(filters.parentNode);
  const isContextualized = SCOPE_CONTEXTUALIZED_FLAG[scope];

  return {
    page,
    size,
    sort,
    ...(isContextualized !== undefined ? { isContextualized } : {}),
    ...(toNonEmptyString(filters.nodeName) !== ""
      ? { nodeName: filters.nodeName }
      : {}),
    ...(toNonEmptyString(filters.sourceSystem) !== ""
      ? { sourceConnector: filters.sourceSystem }
      : {}),
    ...(toNonEmptyString(filters.sourceTag) !== ""
      ? { sourceTag: filters.sourceTag }
      : {}),
    ...(toNonEmptyString(filters.contextTag) !== ""
      ? { contextTag: filters.contextTag }
      : {}),
    ...(toNonEmptyString(filters.nodeType) !== "" &&
    filters.nodeType !== FILTER_SELECT_ALL_SENTINEL
      ? { nodeType: filters.nodeType }
      : {}),
    ...(toNonEmptyString(filters.nodeSubType) !== "" &&
    filters.nodeSubType !== FILTER_SELECT_ALL_SENTINEL
      ? { nodeSubType: filters.nodeSubType }
      : {}),
    ...(parentNodeName !== "" ? { parentNodeName } : {}),
  };
}

export default function NodeList() {
  const { userProfile, getCustomerName, getCustomerCode } = useContext(UserContext);
  const customerName = getCustomerName?.() ?? "";
  const profile = userProfile?.profile;
  const { canAccessPage, usesEffectiveAccess } = useEffectiveAccess();
  const canAccessDataManagement = hasDataManagementAccess(profile, {
    usesEffectiveAccess,
    canAccessPage,
  });
  const [scope, setScope] = useState(TAGS_SCOPE.ALL);
  const scopeRef = useRef(TAGS_SCOPE.ALL);
  const [scopeCounts, setScopeCounts] = useState({
    all: 0,
    contextualized: 0,
    uncontextualized: 0,
  });
  const [grandTotal, setGrandTotal] = useState(0);
  const tagsTableRemountKey = `${getCustomerCode?.() ?? "none"}:${scope}`;

  const [rows, setRows] = useState([]);
  const [tagSubtypeOptions, setTagSubtypeOptions] = useState([]);
  const [nodeTypeOptions, setNodeTypeOptions] = useState([]);
  const [subtypeOptionsByNodeType, setSubtypeOptionsByNodeType] = useState({});
  const [referencePayload, setReferencePayload] = useState(null);
  const [parentCandidates, setParentCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingCsv, setExportingCsv] = useState(false);
  const exportPollTokensRef = useRef(new Map());
  const [pageIndex, setPageIndex] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortValue, setSortValue] = useState(DEFAULT_SORT);
  const [totalRows, setTotalRows] = useState(0);
  const { toast, showSuccess, showFailure, dismissToast } =
    useUserPageToast();

  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditRows, setBulkEditRows] = useState([]);
  const [tagDetailsRow, setTagDetailsRow] = useState(null);
  const [tagDetailsSaving, setTagDetailsSaving] = useState(false);
  const [tableFilters, setTableFilters] = useState({});
  const activeFiltersRef = useRef({});
  const parentCatalogFetchedRef = useRef(false);
  const getCustomerCodeRef = useRef(getCustomerCode);

  useEffect(() => {
    getCustomerCodeRef.current = getCustomerCode;
  }, [getCustomerCode]);

  const tagColumns = useMemo(
    () =>
      buildTagColumns({
        nodeTypeOptions,
        allSubtypeOptions: tagSubtypeOptions,
        customerName,
      }),
    [nodeTypeOptions, tagSubtypeOptions, customerName]
  );

  const loadReferenceData = useCallback(async () => {
    const referenceResult = await fetchNodeTypesReference();
    if (referenceResult.data && !referenceResult.error) {
      const data = referenceResult.data;
      setReferencePayload(data);
      setTagSubtypeOptions(buildTagSubtypeOptions(data));
      setNodeTypeOptions(buildNodeTypeOptions(data));
      setSubtypeOptionsByNodeType(buildSubtypeOptionsByNodeType(data));
      return data;
    }
    setReferencePayload(null);
    return null;
  }, []);

  const loadTagRows = useCallback(
    async (
      filters = {},
      page = 0,
      size = 25,
      sort = DEFAULT_SORT,
      activeScope = scopeRef.current
    ) => {
      const customerCode = getCustomerCodeRef.current?.();
      if (!customerCode) {
        setLoading(false);
        showFailure("No tenant selected. Sign in or pick a customer.");
        setRows([]);
        setTotalRows(0);
        return null;
      }

      const apiQuery = buildTagListApiQuery(
        filters,
        page,
        size,
        sort,
        activeScope
      );
      const probeScope =
        activeScope === TAGS_SCOPE.ALL
          ? TAGS_SCOPE.CONTEXTUALIZED
          : TAGS_SCOPE.ALL;

      setLoading(true);
      const [tagsResult, probeResult] = await Promise.all([
        fetchTagsPage(apiQuery),
        fetchTagsPage(buildTagListApiQuery(filters, 0, 1, sort, probeScope)),
      ]);

      if (tagsResult.error || tagsResult.status >= 400) {
        showFailure(formatApiError(tagsResult));
        setRows([]);
        setTotalRows(0);
      } else {
        const payload = tagsResult.data ?? {};
        const list = extractPagedNodesList(payload);
        const mappedRows = list.map(mapTagSummaryToRow);
        setRows(mappedRows);

        const totalFromApi = extractTotalRowCountFromPagedPayload(payload);
        const scopeTotal =
          totalFromApi != null ? totalFromApi : mappedRows.length;
        setTotalRows(scopeTotal);

        const probeTotal =
          extractTotalRowCountFromPagedPayload(probeResult.data ?? {}) ?? 0;
        const counts = deriveScopeCounts(activeScope, scopeTotal, probeTotal);
        setScopeCounts(counts);
        if (!hasActiveTagFilters(filters)) {
          setGrandTotal(counts.all);
        }

        setLoading(false);
        return mappedRows;
      }
      setLoading(false);
      return null;
    },
    [showFailure]
  );

  const refreshTagsForCustomerChange = useCallback(async () => {
    parentCatalogFetchedRef.current = false;
    activeFiltersRef.current = {};
    scopeRef.current = TAGS_SCOPE.ALL;
    setScope(TAGS_SCOPE.ALL);
    setTableFilters({});
    setPageIndex(0);
    setSortValue(DEFAULT_SORT);
    setBulkEditOpen(false);
    setBulkEditRows([]);
    dismissToast();

    const customerCode = getCustomerCodeRef.current?.();
    if (!customerCode) {
      setRows([]);
      setTotalRows(0);
      setLoading(false);
      showFailure("No tenant selected. Sign in or pick a customer.");
      setParentCandidates([]);
      return;
    }

    const refPromise = loadReferenceData();
    const tagsPromise = loadTagRows(
      activeFiltersRef.current,
      0,
      rowsPerPage,
      DEFAULT_SORT
    );
    const [ref, mappedRows] = await Promise.all([refPromise, tagsPromise]);
    if (!ref?.nodeTypes) {
      setParentCandidates([]);
      return;
    }
    const contextualOnly = (mappedRows ?? []).filter((r) => !r.uncontextualized);
    setParentCandidates(mergeParentCandidatesFromTagRows(contextualOnly, ref));
  }, [loadReferenceData, loadTagRows, rowsPerPage, dismissToast, showFailure]);

  useEffect(() => {
    const handleCustomerChange = () => {
      refreshTagsForCustomerChange();
    };
    window.addEventListener("customerChanged", handleCustomerChange);
    return () =>
      window.removeEventListener("customerChanged", handleCustomerChange);
  }, [refreshTagsForCustomerChange]);

  const INITIAL_PAGE_SIZE = 25;

  useEffect(() => {
    let cancelled = false;
    parentCatalogFetchedRef.current = false;
    (async () => {
      const refPromise = loadReferenceData();
      const tagsPromise = loadTagRows(
        activeFiltersRef.current,
        0,
        INITIAL_PAGE_SIZE,
        DEFAULT_SORT
      );
      const [ref, mappedRows] = await Promise.all([refPromise, tagsPromise]);
      if (cancelled) return;
      if (!ref?.nodeTypes) {
        setParentCandidates([]);
        return;
      }
      const contextualOnly = (mappedRows ?? []).filter((r) => !r.uncontextualized);
      setParentCandidates(mergeParentCandidatesFromTagRows(contextualOnly, ref));
    })();
    return () => {
      cancelled = true;
    };
  }, [loadReferenceData, loadTagRows]);

  useEffect(() => {
    if ((!bulkEditOpen && !tagDetailsRow) || !referencePayload?.nodeTypes) {
      return undefined;
    }
    if (parentCatalogFetchedRef.current) return undefined;
    let cancelled = false;
    (async () => {
      const types = collectUnionOfAllowedParentTypes(referencePayload);
      const result = await fetchParentCandidates(types);
      if (cancelled) return;
      if (result.error || !Array.isArray(result.data)) return;
      parentCatalogFetchedRef.current = true;
      setParentCandidates((previous) => {
        const byId = new Map(previous.map((c) => [String(c.nodeId), c]));
        for (const c of result.data) {
          byId.set(String(c.nodeId), c);
        }
        return [...byId.values()];
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [bulkEditOpen, tagDetailsRow, referencePayload]);

  const handleTagRowClick = useCallback((row) => {
    setTagDetailsRow(row);
  }, []);

  const handleTagDetailsClose = useCallback(() => {
    setTagDetailsRow(null);
  }, []);

  const contextualizeSingleRow = useCallback(async (row, patch) => {
    const tags = buildUnifiedContextualizeTagsFromRows([row]);
    if (!tags.length) {
      showFailure(
        "This row cannot be contextualized (missing source or source path)."
      );
      return false;
    }
    const body = { tags };
    const nodeSubType = toNonEmptyString(patch?.nodeSubType);
    if (nodeSubType) body.nodeSubType = nodeSubType;
    const nodeName = toNonEmptyString(patch?.nodeName);
    if (nodeName) {
      const nodeNameError = validateNodeNameForSave(nodeName);
      if (nodeNameError) {
        showFailure(nodeNameError);
        return false;
      }
      body.nodeName = nodeName;
    } else {
      showFailure("Node name is required to contextualize the row.");
      return false;
    }
    if (patch?.parentNodeId != null && patch.parentNodeId !== "") {
      const parsedParentId = Number(patch.parentNodeId);
      if (isFiniteNumber(parsedParentId)) {
        body.parentNodeId = parsedParentId;
      }
    }
    const bulkResult = await bulkContextualizeUnifiedTags(body);
    if (bulkResult.error || (bulkResult.status != null && bulkResult.status >= 400)) {
      showFailure(formatApiError(bulkResult));
      return false;
    }
    const payload = bulkResult.data;
    if (
      bulkResult.status === 207 ||
      (payload && typeof payload === "object" && payload.success === false)
    ) {
      showFailure(formatBulkContextualizeSummary(payload));
      return false;
    }
    return true;
  }, [showFailure]);

  const handleTagDetailsSave = useCallback(
    async (patch, contextNodeIdFromDrawer) => {
      if (!tagDetailsRow) return;
      setTagDetailsSaving(true);
      dismissToast();
      try {
        if (tagDetailsRow.uncontextualized) {
          const ok = await contextualizeSingleRow(tagDetailsRow, patch);
          if (!ok) return;
          showSuccess(TAGS_UI.toast.contextualized);
        } else {
          let contextNodeId = contextNodeIdFromDrawer;
          if (contextNodeId == null) {
            contextNodeId = await resolveContextNodeIdForTagRowAsync(
              tagDetailsRow
            );
          }
          if (contextNodeId == null) {
            showFailure(TAGS_UI.toast.resolveContextNodeId);
            return;
          }
          if (patch?.nodeName != null) {
            const nodeNameError = validateNodeNameForSave(patch.nodeName);
            if (nodeNameError) {
              showFailure(nodeNameError);
              return;
            }
          }
          const body = buildPatchBodyForBulkUpdate([contextNodeId], patch);
          const result = await patchContextNodes(body);
          if (result.error) {
            showFailure(formatApiError(result));
            return;
          }
          if (result.data?.success === false) {
            showFailure(formatApiError(result));
            return;
          }
          showSuccess(TAGS_UI.toast.updated);
        }
        handleTagDetailsClose();
        await loadTagRows(
          activeFiltersRef.current,
          pageIndex,
          rowsPerPage,
          sortValue
        );
      } finally {
        setTagDetailsSaving(false);
      }
    },
    [
      tagDetailsRow,
      handleTagDetailsClose,
      loadTagRows,
      pageIndex,
      rowsPerPage,
      sortValue,
      contextualizeSingleRow,
      showSuccess,
      showFailure,
      dismissToast,
    ]
  );

  const getRowId = useCallback(
    (row) => row.rowKey ?? String(row.id ?? ""),
    []
  );

  const isBulkEditAllowed = useCallback((selectedRows) => {
    if (!isTagsBulkEditSelectionAllowed(selectedRows)) return false;
    const allUnctx =
      selectedRows.length > 0 &&
      selectedRows.every((r) => r.uncontextualized);
    if (allUnctx) {
      return selectedRows.every(canBulkContextualizeTagRow);
    }
    return true;
  }, []);

  const bulkEditLabel = useCallback(
    (selectedCount, selectedRows) =>
      TAGS_UI.bulkEditLabel(selectedCount, selectedRows),
    []
  );

  const handleBulkEdit = useCallback((selectedRows) => {
    setBulkEditRows(selectedRows);
    setBulkEditOpen(true);
  }, []);

  const handleBulkEditClose = useCallback(() => {
    setBulkEditOpen(false);
    setBulkEditRows([]);
  }, []);

  const handleBulkApply = useCallback(
    async (patch) => {
      dismissToast();
      const isAllUncontextualizedSelection =
        bulkEditRows.length > 0 && bulkEditRows.every((r) => r.uncontextualized);
      if (isAllUncontextualizedSelection) {
        const tags = buildUnifiedContextualizeTagsFromRows(bulkEditRows);
        if (!tags.length) {
          showFailure("No valid unmapped nodes in the selection.");
          return;
        }
        if (tags.length > BULK_CONTEXTUALIZE_MAX_TAGS) {
          showFailure(
            `At most ${BULK_CONTEXTUALIZE_MAX_TAGS} nodes can be contextualized at once (selected ${tags.length}).`
          );
          return;
        }
        const body = { tags };
        const nodeSubType = toNonEmptyString(patch?.nodeSubType);
        if (nodeSubType) body.nodeSubType = nodeSubType;
        const nodeName = toNonEmptyString(patch?.nodeName);
        if (nodeName) {
          const nodeNameError = validateNodeNameForSave(nodeName);
          if (nodeNameError) {
            showFailure(nodeNameError);
            return;
          }
          body.nodeName = nodeName;
        }
        if (patch?.parentNodeId != null && patch.parentNodeId !== "") {
          const parsedParentId = Number(patch.parentNodeId);
          if (isFiniteNumber(parsedParentId)) {
            body.parentNodeId = parsedParentId;
          }
        }

        const bulkResult = await bulkContextualizeUnifiedTags(body);
        if (bulkResult.error || (bulkResult.status != null && bulkResult.status >= 400)) {
          showFailure(formatApiError(bulkResult));
          return;
        }
        const payload = bulkResult.data;
        if (
          bulkResult.status === 207 ||
          (payload && typeof payload === "object" && payload.success === false)
        ) {
          showFailure(formatBulkContextualizeSummary(payload));
        } else {
          showSuccess(formatBulkContextualizeSummary(payload));
        }
        handleBulkEditClose();
        await loadTagRows(
          activeFiltersRef.current,
          pageIndex,
          rowsPerPage,
          sortValue
        );
        return;
      }

      const contextNodeIds = [];
      for (const row of bulkEditRows) {
        if (row.uncontextualized) continue;
        const contextNodeId = await resolveContextNodeIdForTagRowAsync(row);
        if (contextNodeId == null) {
          showFailure(TAGS_UI.toast.resolveContextNodeIds);
          return;
        }
        contextNodeIds.push(contextNodeId);
      }
      if (!contextNodeIds.length) {
        showFailure(TAGS_UI.toast.resolveContextNodeIds);
        return;
      }

      if (patch?.nodeName != null) {
        const nodeNameError = validateNodeNameForSave(patch.nodeName);
        if (nodeNameError) {
          showFailure(nodeNameError);
          return;
        }
      }

      const body = buildPatchBodyForBulkUpdate(contextNodeIds, patch);
      const result = await patchContextNodes(body);
      if (result.error) {
        showFailure(formatApiError(result));
        return;
      }
      if (result.data?.success === false) {
        showFailure(formatApiError(result));
        return;
      }
      showSuccess(TAGS_UI.toast.bulkUpdated(contextNodeIds.length));
      handleBulkEditClose();
      await loadTagRows(
        activeFiltersRef.current,
        pageIndex,
        rowsPerPage,
        sortValue
      );
    },
    [
      bulkEditRows,
      loadTagRows,
      pageIndex,
      rowsPerPage,
      sortValue,
      handleBulkEditClose,
      showSuccess,
      showFailure,
      dismissToast,
    ]
  );

  useEffect(() => {
    return () => {
      exportPollTokensRef.current.forEach((token) => {
        token.cancelled = true;
      });
      exportPollTokensRef.current.clear();
    };
  }, []);

  const startBackgroundExportPoll = useCallback(
    (jobId) => {
      const token = { cancelled: false };
      exportPollTokensRef.current.set(jobId, token);

      (async () => {
        try {
          const job = await pollPlatformJobUntilTerminal(jobId, {
            shouldCancel: () => token.cancelled,
          });
          if (token.cancelled || !job) {
            return;
          }

          const state = extractExportJobState(job);
          if (state === TAGS_EXPORT_JOB_STATES.COMPLETED) {
            const downloadUrl = getJobExportDownloadUrl(job);
            const fileName = getJobExportFileName(job);
            if (!downloadUrl || !fileName) {
              showFailure(TAGS_UI.toast.exportDownloadUnavailable);
              return;
            }
            await triggerExportDownload(downloadUrl, fileName);
            return;
          }

          if (state === TAGS_EXPORT_JOB_STATES.FAILED) {
            showFailure(
              readJobFailureMessage(job) ?? TAGS_UI.toast.exportFailed
            );
          }
        } catch (err) {
          if (!token.cancelled) {
            showFailure(readJobApiErrorMessage(err, TAGS_UI.toast.exportFailed));
          }
        } finally {
          exportPollTokensRef.current.delete(jobId);
          if (!token.cancelled) {
            setExportingCsv(false);
          }
        }
      })();
    },
    [showFailure]
  );

  const handleTagsExportWithSource = useCallback(
    async (sourceConnector) => {
      dismissToast();
      setExportingCsv(true);
      try {
        const { data, status } = await postExportJob(sourceConnector);
        if (status != null && status >= 400) {
          showFailure(readJobApiErrorMessage({ response: { data } }));
          setExportingCsv(false);
          return;
        }

        const jobId = extractJobIdFromResponse(data);
        if (!jobId) {
          showFailure(TAGS_UI.toast.exportFailed);
          setExportingCsv(false);
          return;
        }

        startBackgroundExportPoll(jobId);
      } catch (err) {
        showFailure(readJobApiErrorMessage(err, TAGS_UI.toast.exportFailed));
        setExportingCsv(false);
      }
    },
    [dismissToast, showFailure, startBackgroundExportPoll]
  );

  const tagsExportToolbarAction = useMemo(() => {
    if (!canAccessDataManagement) {
      return null;
    }
    return (
      <NodeListExportCsvMenu
        onSelect={handleTagsExportWithSource}
        disabled={loading || exportingCsv}
        disabledTitle={loading ? "Loading nodes…" : ""}
        loading={exportingCsv}
        options={TAGS_EXPORT_SOURCE_OPTIONS}
      />
    );
  }, [
    canAccessDataManagement,
    exportingCsv,
    handleTagsExportWithSource,
    loading,
  ]);

  const handleTableFilterChange = useCallback(
    (nextFilters) => {
      activeFiltersRef.current = nextFilters;
      setTableFilters(nextFilters);
      setPageIndex(0);
      loadTagRows(nextFilters, 0, rowsPerPage, sortValue);
    },
    [loadTagRows, rowsPerPage, sortValue]
  );

  const handleScopeChange = useCallback(
    (nextScope) => {
      if (nextScope === scopeRef.current) return;
      scopeRef.current = nextScope;
      setScope(nextScope);
      setPageIndex(0);
      setSortValue(DEFAULT_SORT);
      loadTagRows(
        activeFiltersRef.current,
        0,
        rowsPerPage,
        DEFAULT_SORT,
        nextScope
      );
    },
    [loadTagRows, rowsPerPage]
  );

  const handleClearFilters = useCallback(() => {
    activeFiltersRef.current = {};
    setTableFilters({});
    setPageIndex(0);
    loadTagRows({}, 0, rowsPerPage, sortValue);
  }, [loadTagRows, rowsPerPage, sortValue]);

  const filtersActive = hasActiveTagFilters(tableFilters);

  const renderTagsEmptyState = useCallback(() => {
    return (
      <DataTableEmptyState
        title={
          filtersActive
            ? TAGS_UI.emptyFilteredTitle
            : TAGS_UI.emptyTitle
        }
      />
    );
  }, [filtersActive]);

  const tagsTotalLabel = useMemo(() => {
    const shown = Number(totalRows || 0).toLocaleString("en-US");
    const narrowed = filtersActive || scope !== TAGS_SCOPE.ALL;
    if (!narrowed || !grandTotal) return `${shown} nodes`;
    return `${shown} of ${Number(grandTotal).toLocaleString("en-US")} nodes`;
  }, [totalRows, filtersActive, scope, grandTotal]);

  const handlePageChange = useCallback(
    (nextPage) => {
      setPageIndex(nextPage);
      loadTagRows(activeFiltersRef.current, nextPage, rowsPerPage, sortValue);
    },
    [loadTagRows, rowsPerPage, sortValue]
  );

  const handleRowsPerPageChange = useCallback(
    (nextRowsPerPage) => {
      setRowsPerPage(nextRowsPerPage);
      setPageIndex(0);
      loadTagRows(activeFiltersRef.current, 0, nextRowsPerPage, sortValue);
    },
    [loadTagRows, sortValue]
  );

  const handleSortChange = useCallback(
    (columnId, sortOrder) => {
      const apiSortField = SORT_COLUMN_TO_API[columnId] ?? columnId;
      const nextSortValue = `${apiSortField},${sortOrder}`;
      setSortValue(nextSortValue);
      setPageIndex(0);
      loadTagRows(activeFiltersRef.current, 0, rowsPerPage, nextSortValue);
    },
    [loadTagRows, rowsPerPage]
  );

  return (
    <TagsPageRoot data-testid="tags-page">
      <Suspense fallback={null}>
        <Toast
          type={toast.type}
          title={toast.title}
          description={toast.description}
          show={toast.show}
          onClose={dismissToast}
          duration={USER_PAGE_TOAST.DURATION}
        />
      </Suspense>
      <PageTitle
        title={TAGS_UI.pageTitle}
        subTitle={TAGS_UI.pageSubtitle}
        icon={<StorageRoundedIcon />}
      />

      <NodeListDetailsDrawer
        open={Boolean(tagDetailsRow)}
        row={tagDetailsRow}
        onClose={handleTagDetailsClose}
        onSave={handleTagDetailsSave}
        saving={tagDetailsSaving}
        nodeTypeOptions={nodeTypeOptions}
        subtypeOptionsByNodeType={subtypeOptionsByNodeType}
        referencePayload={referencePayload}
        parentCandidates={parentCandidates}
      />

      <BulkEditNodeListDialog
        open={bulkEditOpen}
        onClose={handleBulkEditClose}
        selectedRows={bulkEditRows}
        onApply={handleBulkApply}
        nodeTypeOptions={nodeTypeOptions}
        subtypeOptionsByNodeType={subtypeOptionsByNodeType}
        referencePayload={referencePayload}
        parentCandidates={parentCandidates}
      />

      <TagsTableFrame>
        <DataTable
          key={tagsTableRemountKey}
          title="Node List"
          loading={loading}
          titleAccessory={
            <TagsCountBadge aria-live="polite">{tagsTotalLabel}</TagsCountBadge>
          }
          columns={tagColumns}
          rows={rows}
          serverMode
          totalRows={totalRows}
          page={pageIndex}
          getRowId={getRowId}
          tableMinWidth={TAGS_TABLE_MIN_WIDTH}
          defaultRowsPerPage={rowsPerPage}
          defaultSortProperty="nodeName"
          defaultSortOrder="asc"
          selectable
          onBulkEdit={handleBulkEdit}
          bulkEditLabel={bulkEditLabel}
          isBulkEditAllowed={isBulkEditAllowed}
          bulkEditBlockedTooltip={BULK_EDIT_BLOCKED_TOOLTIP}
          showExport={canAccessDataManagement}
          exportToolbarAction={tagsExportToolbarAction}
          onRowClick={handleTagRowClick}
          onFilterChange={handleTableFilterChange}
          filterValues={tableFilters}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortChange}
          applyTextFiltersOnCommit
          renderEmptyState={renderTagsEmptyState}
          toolbarActions={
            <>
              <ToolbarClearFiltersButton
                variant="outlined"
                $active={filtersActive}
                disabled={!filtersActive}
                onClick={handleClearFilters}
                title={filtersActive ? TAGS_UI.clearFiltersLabel : undefined}
                startIcon={<CloseIcon aria-hidden />}
              >
                <span className={TOOLBAR_BUTTON_LABEL_CLASS}>
                  {TAGS_UI.clearFiltersLabel}
                </span>
              </ToolbarClearFiltersButton>
              <TagsScopeTabs
                role="group"
                aria-label="Filter by contextualization"
              >
                {TAGS_SCOPE_TABS.map((tab) => (
                  <TagsScopeTab
                    key={tab.value}
                    type="button"
                    $active={scope === tab.value}
                    aria-pressed={scope === tab.value}
                    onClick={() => handleScopeChange(tab.value)}
                    data-testid={`tags-scope-${tab.value}`}
                  >
                    {tab.label}
                    <TagsScopeTabBadge>
                      {Number(scopeCounts[tab.value] || 0).toLocaleString(
                        "en-US"
                      )}
                    </TagsScopeTabBadge>
                  </TagsScopeTab>
                ))}
              </TagsScopeTabs>
            </>
          }
          testId="tags-table"
        />
      </TagsTableFrame>
    </TagsPageRoot>
  );
}
